import { useQuery } from "@tanstack/react-query";
import { useState, useRef, useEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { CardImage } from "./card-image";
import { fmtUsd } from "./data-table";

interface PreviewCard {
  slabId: string;
  tokenId: string;
  description: string;
  imageUrl: string;
  rarity: string;
  valueUsd: number;
}

interface PreviewResponse {
  machineId: string;
  totalSlabs: number;
  remainingCount: number;
  remainingByRarity: Record<string, number>;
  topUnpulled: PreviewCard[];
  recentPulled: Array<PreviewCard & { pullDate: number }>;
}

// Desktop panel width; on narrow screens we clamp to the viewport.
const PANEL_WIDTH_DESKTOP = 420;
const PANEL_GAP = 8;
const VIEWPORT_MARGIN = 12;

function isTouchPrimary(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia("(hover: none)").matches;
}

function getPanelWidth(): number {
  if (typeof window === "undefined") return PANEL_WIDTH_DESKTOP;
  // Leave a 12px gutter on each side. Cap at 420 for large screens.
  return Math.min(PANEL_WIDTH_DESKTOP, window.innerWidth - VIEWPORT_MARGIN * 2);
}

/**
 * Wraps any child (typically a pack row or pack name) with a hover-triggered
 * floating preview of the highest-value cards still Unpulled in that
 * SlabMachine. Renders via a document.body portal so parent `overflow: hidden`
 * (e.g. our .scan-sweep panels) can't clip it.
 */
export function PackHoverPreview({
  chainId,
  machineAddress,
  children,
}: {
  chainId: number;
  machineAddress: string;
  children: ReactNode;
}) {
  const machineId = `${chainId}_${machineAddress.toLowerCase()}`;
  const [open, setOpen] = useState(false);
  const [hasHovered, setHasHovered] = useState(false);
  const [mobile, setMobile] = useState<boolean>(() => isTouchPrimary());
  const [pos, setPos] = useState<{ top: number; left: number }>({ top: 0, left: 0 });
  const triggerRef = useRef<HTMLSpanElement>(null);
  const openTimer = useRef<number | undefined>(undefined);
  const closeTimer = useRef<number | undefined>(undefined);

  // Keep `mobile` in sync if the user rotates the device / switches pointer
  // modes. We use matchMedia rather than a width query so touch tablets
  // get the modal UX too.
  useEffect(() => {
    const mql = window.matchMedia("(hover: none)");
    const update = () => setMobile(mql.matches);
    mql.addEventListener?.("change", update);
    return () => mql.removeEventListener?.("change", update);
  }, []);

  const { data, isLoading } = useQuery<PreviewResponse>({
    queryKey: ["pack-preview", machineId],
    queryFn: async () => {
      const res = await fetch(`/api/pack-preview?machineId=${machineId}&limit=10`);
      return res.json();
    },
    enabled: hasHovered,
    staleTime: 60_000,
  });

  function computePosition() {
    const el = triggerRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const panelW = getPanelWidth();
    // Clamp horizontally so the panel doesn't spill off-screen.
    const maxLeft = window.innerWidth - panelW - VIEWPORT_MARGIN;
    const left = Math.min(
      Math.max(VIEWPORT_MARGIN, rect.left),
      Math.max(VIEWPORT_MARGIN, maxLeft)
    );
    // If no room below, open above the trigger.
    const estHeight = 320;
    const below = rect.bottom + PANEL_GAP;
    const fitsBelow = below + estHeight < window.innerHeight;
    const top = fitsBelow ? below : Math.max(VIEWPORT_MARGIN, rect.top - estHeight - PANEL_GAP);
    setPos({ top, left });
  }

  function onEnter() {
    window.clearTimeout(closeTimer.current);
    openTimer.current = window.setTimeout(() => {
      computePosition();
      setOpen(true);
      setHasHovered(true);
    }, 180);
  }
  function onLeave() {
    window.clearTimeout(openTimer.current);
    closeTimer.current = window.setTimeout(() => setOpen(false), 120);
  }

  // Mobile has no hover — tap the trigger to toggle the preview open/closed.
  function onTriggerClick(e: React.MouseEvent) {
    // Only short-circuit the click for touch-primary devices. Mouse users
    // should still get the hover behavior without the tap eating their click.
    if (mobile) {
      e.preventDefault();
      e.stopPropagation();
      window.clearTimeout(openTimer.current);
      window.clearTimeout(closeTimer.current);
      if (open) {
        setOpen(false);
      } else {
        // Mobile opens as a centered modal, no position math needed.
        setOpen(true);
        setHasHovered(true);
      }
    }
  }

  // Reposition on scroll / resize while open (desktop tooltip only).
  // The mobile modal is centered and doesn't track a trigger.
  useEffect(() => {
    if (!open || mobile) return;
    function update() {
      computePosition();
    }
    window.addEventListener("scroll", update, true);
    window.addEventListener("resize", update);
    return () => {
      window.removeEventListener("scroll", update, true);
      window.removeEventListener("resize", update);
    };
  }, [open, mobile]);

  useEffect(
    () => () => {
      window.clearTimeout(openTimer.current);
      window.clearTimeout(closeTimer.current);
    },
    []
  );

  const panelInner = (
    <div className="pointer-events-auto rounded-md border border-accent/60 bg-panel/95 backdrop-blur-md p-3 shadow-glowStrong brackets max-h-[85vh] overflow-y-auto">
        <div className="mb-2 flex items-center justify-between gap-2">
          <div className="text-[10px] font-mono uppercase tracking-wider text-accent2 truncate">
            <span className="text-accent">›</span> loot.preview
            <span className="animate-blink text-accent ml-1">_</span>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            {data && (
              <div className="text-[10px] font-mono text-muted">
                <span className="text-accent">{data.remainingCount}</span>/{data.totalSlabs}
              </div>
            )}
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                setOpen(false);
              }}
              className="rounded border border-border px-1.5 py-0.5 text-[10px] font-mono text-muted hover:border-accent hover:text-accent transition-colors"
              aria-label="Close preview"
            >
              ✕
            </button>
          </div>
        </div>

        {isLoading || !data ? (
          <div className="flex items-center justify-center py-8 text-xs text-muted font-mono">
            <span className="mr-2 inline-block h-4 w-4 rounded-full border-2 border-accent/30 border-t-accent animate-radar" />
            scanning machine inventory…
          </div>
        ) : data.topUnpulled.length === 0 ? (
          <div className="py-6 text-center text-xs text-muted font-mono">
            <span className="text-danger">›</span> machine empty
          </div>
        ) : (
          <>
            <div className="mb-2 flex flex-wrap gap-1">
              {Object.entries(data.remainingByRarity).map(([rarity, count]) => (
                <span
                  key={rarity}
                  className={
                    "rounded border px-1.5 py-0.5 text-[9px] font-mono uppercase tracking-wider " +
                    rarityColorClass(rarity)
                  }
                >
                  {rarity} × {count}
                </span>
              ))}
            </div>

            <div className="grid grid-cols-3 sm:grid-cols-5 gap-1.5">
              {data.topUnpulled.map((c, i) => (
                <div
                  key={c.slabId}
                  className="group relative overflow-hidden rounded border border-border bg-bg hover:border-accent transition-colors"
                  style={{
                    animation: `fadeIn 0.3s ease-out ${i * 40}ms both`,
                  }}
                  title={c.description}
                >
                  <div className="aspect-[3/4] w-full">
                    <CardImage src={c.imageUrl} alt={c.description} size={160} />
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-bg via-bg/85 to-transparent p-1">
                    <div className="text-[9px] font-mono font-bold text-accent neon-text truncate">
                      {fmtUsd(c.valueUsd)}
                    </div>
                    <div
                      className={`text-[8px] uppercase tracking-wider truncate ${rarityTextClass(
                        c.rarity
                      )}`}
                    >
                      {c.rarity}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="mt-2 border-t border-border/60 pt-1.5 text-[9px] font-mono text-muted">
              <span className="text-accent2">›</span> top-value cards still in machine
            </div>
          </>
        )}
    </div>
  );

  // Desktop: anchored tooltip following the trigger.
  // Mobile (touch-primary): centered bottom-sheet modal with a dimmed backdrop
  // so it never climbs over the app header.
  const panel = !open ? null : mobile ? (
    <div
      data-pack-preview-panel="true"
      className="fixed inset-0 z-[9999] flex items-end justify-center animate-fadeIn"
      role="dialog"
      aria-modal="true"
    >
      {/* backdrop — tap to dismiss */}
      <div
        className="absolute inset-0 bg-bg/75 backdrop-blur-sm"
        onClick={() => setOpen(false)}
      />
      <div
        className="relative w-full max-w-[480px] px-3 pb-3"
        style={{ paddingBottom: `calc(env(safe-area-inset-bottom, 0px) + 12px)` }}
      >
        {panelInner}
      </div>
    </div>
  ) : (
    <div
      data-pack-preview-panel="true"
      className="pointer-events-none fixed z-[9999] animate-fadeIn"
      style={{ top: pos.top, left: pos.left, width: getPanelWidth() }}
      role="tooltip"
      onMouseEnter={() => window.clearTimeout(closeTimer.current)}
      onMouseLeave={onLeave}
    >
      {panelInner}
    </div>
  );

  return (
    <>
      <span
        ref={triggerRef}
        className="relative inline-block"
        onMouseEnter={onEnter}
        onMouseLeave={onLeave}
        onFocus={onEnter}
        onBlur={onLeave}
        onClick={onTriggerClick}
      >
        {children}
      </span>
      {panel && createPortal(panel, document.body)}
    </>
  );
}

function rarityColorClass(rarity: string): string {
  switch (rarity) {
    case "Legendary":
      return "border-legendary/60 bg-legendary/10 text-legendary";
    case "Mythic":
      return "border-mythic/60 bg-mythic/10 text-mythic";
    case "Rare":
      return "border-rare/60 bg-rare/10 text-rare";
    case "Uncommon":
      return "border-uncommon/60 bg-uncommon/10 text-uncommon";
    default:
      return "border-common/40 bg-common/10 text-common";
  }
}

function rarityTextClass(rarity: string): string {
  switch (rarity) {
    case "Legendary":
      return "text-legendary";
    case "Mythic":
      return "text-mythic";
    case "Rare":
      return "text-rare";
    case "Uncommon":
      return "text-uncommon";
    default:
      return "text-common";
  }
}
